/* eslint-disable no-unused-vars */
import React, { Component, useState, useEffect } from 'react'

import { gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { Box, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, Paper, Typography, Button } from '@material-ui/core'

import { Loading } from '../../components/Loading/Loading'
import PeriodNavigator from '../../components/PeriodPicker/PeriodNavigator'
import { PERIOD_BOUNDS } from '../../components/PeriodPicker/usePeriodBounds'
import CurrencyPicker from '../../components/Pickers/CurrencyPicker'
import { DomainPicker } from '../../components/Pickers/DomainPicker'
import GraphDrawer from './viewExpense'
import { useCurrentUser, useLastStartedCompanyPeriod } from '../../redux/selectors'
import { longMonthNames } from '../../components/DateTools/MonthsNames'

const initialQuery = gql`
    query($period: Period!, $domain: String) {
        activeCompanyVendors(period: $period, domain: $domain) {
            id
            name
            country {
                name
            }
        }
    }
`

const REPORTS_QUERY = gql`
    query($period: Period, $vendors: [Int], $currency:String) {
        totalExpense(period: $period, vendors: $vendors, currency:$currency) {
            current
            threeMonths
            sixMonths
        }
        serviceNumbersCount(period: $period, vendors: $vendors) {
            current
            threeMonths
            sixMonths
        }
        serviceNumbersCost(period: $period, vendors: $vendors, currency:$currency) {
            current
            threeMonths
            sixMonths
        }
        expenseByTechnology(period: $period, vendors: $vendors, currency:$currency) {
            period
            dataByTechnology {
                technology
                totalCost
            }
        }
        expenseByVendor(period: $period, vendors: $vendors, currency:$currency) {
            dataByPeriod {
                period
                dataByVendor {
                    vendor {
                        name
                        id
                    }
                    totalCost
                    dataByChargeGroup {
                        chargeGroup {
                            name
                            id
                        }
                        cost
                    }
                }
            }
        }
        totalUsageByPeriod(period:$period,vendors:$vendors){
            period
            totalUsage{
                voice{
                    unit{
                        name
                    }
                    count
                }
                internet{
                    unit{
                        name
                    }
                    count
                }
                sms{
                    unit{
                        name
                    }
                    count
                }
            }
        }
        totalCountByVendor(period: $period, vendors: $vendors) {
            period
            dataByVendor {
                count
                vendor {
                    name
                    id
                }
            }
        }
    }
`

function getItemsFrom(obj) {
    return Object.keys(obj).map(
        function (key, index) {
            return { key: key, value: obj[key] }
        }
    )
}

class MultipleGroupedSelector extends Component {
    constructor(props) {
        super(props)
        this.state = {
            allVendors: this.props.allVendors,
            selectedElements: this.props.allVendors,
        }
    }

    selectAll = () => {
        const allIds = Object.values(this.props.items).flat().map(v => v.id).sort()
        this.setState({ selectedElements: allIds })
        this.props.onChange(allIds)
    }

    deselectAll = () => {
        this.setState({ selectedElements: [] })
        this.props.onChange([])
    }

    componentDidMount() {
        this.props.onChange(this.state.selectedElements)
    }

    onCheckboxChange = (id) => {
        let selectedElements = [...this.state.selectedElements]
        if (selectedElements.includes(id)) {
            selectedElements.splice(selectedElements.indexOf(id), 1)
        } else {
            selectedElements.push(id)
        }
        selectedElements.sort()
        this.setState(state => { state.selectedElements = selectedElements; return state })
        this.props.onChange(selectedElements)
    }

    render() {
        const { t } = this.props
        return (
            <div className='w-100 mh-100' key="vendor_container_001">
                <Typography>{t("Vendors")}</Typography>
                <Divider className="bg-light-blue mt-3 mb-4" />
                <Box className='w-100'>
                    <Button onClick={this.selectAll} style={{ float: "left", display: "flex", margin: "0.0" }} size="small" color="primary" variant="contained">{t("fieldLabels:Select All")}</Button>
                    <Button onClick={this.deselectAll} style={{ float: "right", display: "flex", margin: "0.0" }} size="small" color="primary" variant="outlined">{t("fieldLabels:Deselect All")}</Button>
                </Box>
                <FormControl className="mt-3 w-100 mh-100 overflow-auto">
                    <FormGroup id="vendors_chart_list_container" className='w-100 overflow-auto'>
                        {getItemsFrom(this.props.items).map(
                            ({ key, value }, index) => (
                                <div key={"vendor_country__" + index} className="d-flex flex-column w-100">
                                    <Typography className="dashboard-vendor-name" key={key}>{key}</Typography>
                                    {value.map((data, index) => (
                                        <FormControlLabel
                                            className="pl-3"
                                            control={
                                                <Checkbox
                                                    checked={this.state.selectedElements.includes(data.id)}
                                                    onChange={() => this.onCheckboxChange(data.id)}
                                                />
                                            }
                                            label={data.name}
                                            key={"vendor_" + index}
                                        />
                                    ))}
                                </div>
                            )
                        )}
                    </FormGroup>
                </FormControl>
            </div>
        )
    }
}

const FleetHeader = ({ t, year, month, selectedDomain, currencyCode, currencySymbol, onChangeDate, onChangeDomain, onChangeCurrency }) => {
    const months = longMonthNames(t)
    const monthTitle = months[month - 1] + " " + year

    return (
        <div className="d-flex w-100 p-2 align-items-center">
            <Typography className="fleet-month-title mr-auto m-1 ml-3" variant='h4'>
                {monthTitle}
            </Typography>
            <span style={{ display: "flex", gap: "16px", alignItems: "center", color: "gray", fontSize: 18 }}>
                <PeriodNavigator month={month} year={year} onChangePeriod={onChangeDate} bounds={PERIOD_BOUNDS.COMPANY} />
                <DomainPicker t={t} selected={selectedDomain} onChangeDomain={onChangeDomain} />
                <CurrencyPicker selectedSymbol={currencySymbol} selected={currencyCode} onChangeCurrency={onChangeCurrency} month={month} year={year} />
            </span>
        </div>
    )
}

const FleetVendorSelector = ({ t, period, domain, selectedVendors, onChangeVendors }) => (
    <Loading query={initialQuery} variables={{ period, domain }} showPreviousDataWhileLoading={true}>
        {(data) => {
            const vendorByCountry = {}
            const allVendorsId = []
            for (let vendor of data.activeCompanyVendors) {
                if (!(vendor.country.name in vendorByCountry)) {
                    vendorByCountry[vendor.country.name] = []
                }
                vendorByCountry[vendor.country.name].push({ id: vendor.id, name: vendor.name })
                allVendorsId.push(vendor.id)
            }
            return (
                <MultipleGroupedSelector
                    t={t}
                    allVendors={selectedVendors ?? allVendorsId}
                    items={vendorByCountry}
                    onChange={onChangeVendors}
                />
            )
        }}
    </Loading>
)

const FleetReports = ({ t, period, selectedVendors, currencyCode, currencySymbol, year, month }) => {
    const months = longMonthNames(t)
    const dateTitle = months[month - 1] + " " + year

    return (
        <Loading query={REPORTS_QUERY} variables={{ period, vendors: selectedVendors.map(id => parseInt(id)), currency: currencyCode }}>
            {(data) => (
                <GraphDrawer t={t} currencySymbol={currencySymbol} dateTitle={dateTitle} data={data} />
            )}
        </Loading>
    )
}

const Fleet = (props) => {
    const { t } = props
    const LastCompanyPeriod = useLastStartedCompanyPeriod()
    const currentUser = useCurrentUser()

    const [year, setYear] = useState(LastCompanyPeriod.year)
    const [month, setMonth] = useState(LastCompanyPeriod.month)
    const [period, setPeriod] = useState(`${LastCompanyPeriod.year}-${LastCompanyPeriod.month}`)
    const [selectedDomain, setSelectedDomain] = useState(null)
    const [currencyCode, setCurrencyCode] = useState(currentUser.currency?.code ?? null)
    const [currencySymbol, setCurrencySymbol] = useState(currentUser.currency?.symbol ?? null)
    const [selectedVendors, setSelectedVendors] = useState(null)

    useEffect(() => {
        setSelectedVendors(null)
    }, [selectedDomain, period])

    const onChangeDate = (newYear, newMonth) => {
        setYear(newYear)
        setMonth(newMonth)
        setPeriod(`${newYear}-${newMonth}`)
    }

    const onChangeCurrency = (code, symbol) => {
        setCurrencyCode(code)
        setCurrencySymbol(symbol)
    }

    const onChangeVendors = (vendors) => {
        setSelectedVendors([...vendors].sort())
    }

    return (
        <Paper className="w-100 h-auto fleet-container">

            <FleetHeader
                t={t}
                year={year}
                month={month}
                selectedDomain={selectedDomain}
                currencyCode={currencyCode}
                currencySymbol={currencySymbol}
                onChangeDate={onChangeDate}
                onChangeDomain={setSelectedDomain}
                onChangeCurrency={onChangeCurrency}
            />

            <Divider className="bg-light-blue" />

            {selectedDomain !== null && (
                <div className="d-flex w-100">
                    <div className="dashboard-filter-container p-3">
                        <FleetVendorSelector
                            t={t}
                            period={period}
                            domain={selectedDomain}
                            selectedVendors={selectedVendors}
                            onChangeVendors={onChangeVendors}
                        />
                    </div>
                    <div className="flex-grow-1">
                        {selectedVendors !== null && (
                            <FleetReports
                                t={t}
                                period={period}
                                selectedVendors={selectedVendors}
                                currencyCode={currencyCode}
                                currencySymbol={currencySymbol}
                                year={year}
                                month={month}
                            />
                        )}
                    </div>
                </div>
            )}

        </Paper>
    )
}

export default withTranslation('dashBoard')(Fleet)
